'use client';

import { useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { motion } from 'motion/react';
import { RiCheckLine, RiFlashlightLine, RiLockLine, RiMailLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import {
  AuthBrand,
  AuthDivider,
  AuthField,
  FieldError,
  FormAlert,
  GoogleButton,
  PasswordReveal,
  SubmitButton,
  fieldV,
  legalChunks,
} from '@/components/auth-parts';
import { ApiError } from '@/lib/api';
import { Link, useRouter } from '@/i18n/navigation';
import { DASH_HOME } from '@/lib/dash-home';
import { DEMO_EMAIL, DEMO_PASSWORD } from '@/lib/demo';
import { dur, sceneContainer, sceneItem, spring, springFade } from '@/lib/motion';
import { signIn } from '@/lib/session';
import { siteLinks } from '@/lib/site-links';

/**
 * Giriş formu — bölünmüş tuvalin beyaz yarısı.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Ekran açılırken parçalar yukarıdan aşağı sırayla odaklanıyor (`sceneItem`:
 * opaklık + 12px yol + blur). Sıra rastgele değil, formun okunma sırası —
 * kelime işareti, başlık, hızlı yol (Google), ayraç, alanlar, buton. Kullanıcı
 * ilk yarım saniyede formun nerede başlayıp nerede bittiğini görüyor.
 *
 * Hata satırı belirip kaybolurken altındaki alanlar KAYIYOR, ışınlanmıyor —
 * hatanın altında kalan her şeyde `layout="position"` var, Motion yer
 * değiştirmeyi transform'a çeviriyor (SSS akordeonuyla aynı teknik).
 * `top`/`height` animasyonu her karede yeniden düzen demek (DESIGN.md §1).
 *
 * Alan kutusu, göz düğmesi, gönder butonu ve hata bölgeleri `auth-parts.tsx`te
 * — kayıt ve şifre sıfırlama ekranlarıyla ortak. Burada kalan tek şey giriş
 * ekranına ÖZGÜ olan: iki alan, "beni hatırla" ve doğrulama akışı.
 *
 * ── Backend ───────────────────────────────────────────────────────────────
 *
 * Auth uçları henüz yok (CLAUDE.md → Durum). Tek çalışan yol DEMO hesabı:
 * `demo@mentorivo.com` / `demo1234` girilirse (ya da formun üstündeki demo
 * düğmesine basılırsa) tarayıcıda bir demo oturumu açılıp panele geçiliyor.
 * Başka bir e-posta girildiğinde form "servis bağlı değil" diyor — herhangi
 * bir şifreyi kabul eden sahte bir giriş, gerçek bir hesabı varmış hissi
 * verirdi.
 *
 * Uçlar gelince `signIn` içindeki demo dalı `api.post('/auth/login', …)` ile
 * değiştirilecek; formun geri kalanı (doğrulama, hata bölgesi, bekleme
 * durumu, yönlendirme) olduğu gibi kalır.
 */

/** Kabaca "@ var, nokta var, boşluk yok". Asıl doğrulama sunucuda. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Giriş sonrası nereye dönüleceği.
 *
 * `proxy.ts` korumalı bir sayfadan çevirdiğinde adrese `?next=/tr/dashboard`
 * ekliyor. Kullanıcı doğrudan giriş sayfasına geldiyse parametre yok, panele
 * gidiyor.
 *
 * `useSearchParams` yerine `window.location`: o kanca sayfayı Suspense
 * sınırına bağlıyor ve bu bileşen zaten yalnızca istemcide çalışıyor.
 *
 * Yalnızca `/` ile başlayan, `//` ile başlamayan yollar kabul ediliyor —
 * aksi hâlde `?next=https://baska-site` yazan biri, bizim giriş sayfamızı
 * kendi sitesine açılan bir kapıya çevirebilirdi (open redirect).
 */
function returnPath(): string {
  if (typeof window === 'undefined') return DASH_HOME;
  const raw = new URLSearchParams(window.location.search).get('next');
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return DASH_HOME;

  // Adres locale önekli geliyor ("/tr/dashboard"); router zaten öneki kendi
  // ekliyor, iki kez eklenmesin diye soyuyoruz.
  const stripped = raw.replace(/^\/(en|tr)(?=\/|$)/, '');
  return stripped === '' ? DASH_HOME : stripped;
}

type Errors = { email?: string; password?: string; form?: string };

export function LoginForm() {
  const t = useTranslations('login');
  const uid = useId();
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [remember, setRemember] = useState(true);
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  /* İlk denemeden ÖNCE alan alan uyarı basmak, kullanıcı daha e-postasını
     yazarken "geçersiz" demek olur. Denemeden sonra anlık doğrulama açılıyor. */
  const [tried, setTried] = useState(false);

  /* Doğrulama düşerse odak İLK hatalı alana gider. Olmazsa odak butonda
     kalıyor ve klavye/ekran okuyucu kullanıcısı, ekranın yukarısında beliren
     uyarıyı ancak geri gezinerek buluyor. */
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  function validate(next = { email, password }): Errors {
    const found: Errors = {};
    if (!EMAIL.test(next.email.trim())) found.email = t('email.error');
    if (next.password.length === 0) found.password = t('password.error');
    return found;
  }

  function onChange(field: 'email' | 'password', value: string) {
    const next = { email, password, [field]: value };
    if (field === 'email') setEmail(value);
    else setPassword(value);
    if (tried) setErrors(validate(next));
  }

  /**
   * Demo hesabıyla giriş yapar.
   *
   * Demo artık taklit bir çerez değil, veritabanındaki GERÇEK bir hesap
   * (`packages/db/seed`). Tek bir kimlik yolu olması, "demo modunda çalışıp
   * gerçekte çalışmayan" sınıfındaki hataları baştan siliyor.
   *
   * Bekleme durumu KAPATILMIYOR: `router.push` sayfayı değiştirene kadar
   * buton "giriş yapılıyor" halinde kalsın istiyoruz. `setPending(false)`
   * deseydik kullanıcı, panel yüklenirken formu ikinci kez gönderebilirdi.
   */
  async function enterDemo() {
    setErrors({});
    setPending(true);
    try {
      await signIn({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
      router.push(DASH_HOME);
    } catch {
      setPending(false);
      setErrors({ form: t('notConnected') });
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      (found.email ? emailRef : passwordRef).current?.focus();
      return;
    }

    setErrors({});
    setPending(true);

    try {
      await signIn({ email: email.trim().toLowerCase(), password });
      // Bekleme durumu bilerek açık kalıyor — yönlendirme tamamlanana kadar
      // buton kilitli, form ikinci kez gönderilemiyor.
      router.push(returnPath());
    } catch (error) {
      setPending(false);

      // Sunucu hangisinin yanlış olduğunu SÖYLEMİYOR (kayıtlı e-postayı
      // sızdırmamak için); form da aynı dili konuşuyor ve hatayı tek bir
      // alana değil forma yazıyor.
      const message =
        error instanceof ApiError && error.status === 401
          ? t('invalid')
          : error instanceof ApiError && error.status === 429
            ? t('tooMany')
            : t('notConnected');

      setErrors({ form: message });
    }
  }

  const emailId = `${uid}-email`;
  const passwordId = `${uid}-password`;

  return (
    <section className="auth-col">
      <motion.div
        className="auth-form-inner"
        variants={sceneContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={sceneItem}>
          <AuthBrand />
        </motion.div>

        <motion.div className="auth-head" variants={sceneItem}>
          <h1 className="auth-title">{t('title')}</h1>
          <p className="auth-lead">{t('lead')}</p>
        </motion.div>

        <motion.div variants={sceneItem}>
          <GoogleButton label={t('google')} />
        </motion.div>

        {/* Şu an gerçekten çalışan tek giriş yolu. Google düğmesinin hemen
            altında duruyor: bağlı olmayan bir düğmenin altında çalışan bir
            düğme görmek, kullanıcının hangisini deneyeceğini netleştiriyor. */}
        <motion.div variants={sceneItem}>
          <button type="button" className="auth-social auth-demo" onClick={enterDemo}>
            <RiFlashlightLine size={17} className="auth-demo-mark" aria-hidden="true" />
            {t('demo.button')}
          </button>
          <p className="auth-demo-hint">{t('demo.hint')}</p>
        </motion.div>

        <motion.div variants={sceneItem}>
          <AuthDivider label={t('divider')} />
        </motion.div>

        <form className="auth-form" onSubmit={onSubmit} noValidate>
          {/* İlk alan: üstünde konumu değişen bir şey yok, `layout` gerekmiyor —
              o yüzden tam `sceneItem` (y + blur) ile girebiliyor. */}
          <motion.div className="auth-field" variants={sceneItem}>
            <label className="auth-label" htmlFor={emailId}>
              {t('email.label')}
            </label>
            <AuthField
              invalid={Boolean(errors.email)}
              icon={<RiMailLine size={18} aria-hidden="true" />}
            >
              <input
                ref={emailRef}
                id={emailId}
                className="auth-input"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('email.placeholder')}
                value={email}
                onChange={(e) => onChange('email', e.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? `${emailId}-error` : undefined}
              />
            </AuthField>
            <FieldError id={`${emailId}-error`} message={errors.email} />
          </motion.div>

          {/* Buradan aşağısı `layout="position"`: üstteki hata belirince
              KAYARAK yer değiştiriyorlar. "position" bilinçli — `layout` tek
              başına kutunun BOYUTUNU da ölçekler ve içerideki metin o süre
              boyunca gerilir. */}
          <motion.div className="auth-field" variants={fieldV} layout="position">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor={passwordId}>
                {t('password.label')}
              </label>
              <Link href={siteLinks.forgotPassword.href} className="auth-forgot">
                {t('forgot')}
              </Link>
            </div>
            <AuthField
              invalid={Boolean(errors.password)}
              icon={<RiLockLine size={18} aria-hidden="true" />}
            >
              <input
                ref={passwordRef}
                id={passwordId}
                className="auth-input"
                type={reveal ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder={t('password.placeholder')}
                value={password}
                onChange={(e) => onChange('password', e.target.value)}
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? `${passwordId}-error` : undefined}
              />
              <PasswordReveal
                revealed={reveal}
                onToggle={() => setReveal((v) => !v)}
                showLabel={t('password.show')}
                hideLabel={t('password.hide')}
              />
            </AuthField>
            <FieldError id={`${passwordId}-error`} message={errors.password} />
          </motion.div>

          <motion.div className="auth-row" variants={fieldV} layout="position">
            <label className="auth-check">
              <input
                type="checkbox"
                className="auth-check-input"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="auth-check-box" aria-hidden="true">
                {/* Tik ölçek + opaklıkla geliyor; `pathLength` çizimi güzel
                    ama stroke-dashoffset animasyonu transform/opacity/filter
                    üçlüsünün dışında (DESIGN.md §1). */}
                <motion.span
                  className="auth-check-mark"
                  animate={{ scale: remember ? 1 : 0.4, opacity: remember ? 1 : 0 }}
                  transition={springFade(spring.snap, dur.instant)}
                >
                  <RiCheckLine size={14} />
                </motion.span>
              </span>
              <span className="auth-check-text">{t('remember')}</span>
            </label>
          </motion.div>

          <FormAlert message={errors.form} />

          <motion.div variants={fieldV} layout="position">
            <SubmitButton pending={pending} label={t('submit')} pendingLabel={t('submitting')} />
          </motion.div>
        </form>

        <motion.p className="auth-alt" variants={sceneItem}>
          {t('noAccount')}{' '}
          <Link href={siteLinks.register.href} className="auth-alt-link">
            {t('register')}
          </Link>
        </motion.p>

        <motion.p className="auth-legal" variants={sceneItem}>
          {t.rich('legal', legalChunks)}
        </motion.p>
      </motion.div>
    </section>
  );
}
